import {createAction, createAsyncThunk, createSelector, createSlice} from '@reduxjs/toolkit';
import {AsyncState, RejectedState} from '../../models/async_state';
import {Status} from '../../enums/status';
import {OccasionFragment, OccasionsQuery, OccasionType} from '../../gql/pwa/graphql';
import {Occasions} from '../../network/occasions';
import {RootState} from '../store';
import {Filters} from '../../pages/occasions/components/filters.tsx';
import {getFromDatabase, storeArrayInDatabase} from '../../support/database.ts';
import {formatISO} from 'date-fns';

interface State extends OccasionsQuery {
    filteredOccasions: OccasionType[];
    filter: Filters;
    limit: number;
    lastOccasionFetch: string | null;
}

interface Occasion extends OccasionFragment {
    sort?: number;
    id: number;
}

export enum Transmission {
    MANUAL = 'Handgeschakeld',
    AUTOMATIC = 'Automaat',
}

export const emptyFilter = {
    brands: undefined,
    models: undefined,
    buildYearFrom: undefined,
    buildYearTill: undefined,
    locationId: undefined,
    mileageFrom: undefined,
    mileageTill: undefined,
    priceFrom: undefined,
    priceTill: undefined,
    transmission: undefined,
};

export const fetchOccasions = createAsyncThunk('occasions/fetchOccasions', async () => {
    const result = await Occasions.getAllOccasions();
    return result.data;
});

export const filteredOccasionsSelector = (state: RootState) => {
    return filteredOccasions(state);
};

export const filteredOccasionCountSelector = (state: RootState) => {
    return filteredOccasionCount(state);
};

export const filterSingleOccasionSelector = (state: RootState, occasionId: number) => {
    return state.occasions?.occasions?.find((occasion) => occasion?.id === occasionId);
};

export const applyOccasionFilter = (occasions: (OccasionType | null)[], filter: Filters) => {
    return (
        occasions &&
        occasions.filter((occasion) => {
            return (
                (!filter.brands || filter.brands?.find((brand) => occasion?.brand === brand)) &&
                (!filter.models || filter.models?.find((model) => occasion?.model === model)) &&
                (!filter.transmission || filter.transmission === occasion?.transmission) &&
                (!filter.locationId || filter.locationId === occasion?.location_id) &&
                (!filter.buildYearFrom || filter.buildYearFrom >= Number.parseInt(occasion?.build_year || '0', 10)) &&
                (!filter.buildYearTill || filter.buildYearTill <= Number.parseInt(occasion?.build_year || '0', 10)) &&
                (!filter.mileageFrom || filter.mileageFrom <= Number.parseInt(occasion?.mileage || '0', 10)) &&
                (!filter.mileageTill || filter.mileageTill >= Number.parseInt(occasion?.mileage || '0', 10)) &&
                (!filter.priceFrom || filter.priceFrom <= (occasion?.price || 0)) &&
                (!filter.priceTill || filter.priceTill >= (occasion?.price || 0))
            );
        })
    );
};

export const filteredOccasions = createSelector(
    (state: RootState) => {
        return {occasions: state.occasions?.occasions, filter: state.occasions?.filter, limit: state.occasions?.limit};
    },
    (occasions) => {
        return (
            (occasions.occasions &&
                occasions.filter &&
                applyOccasionFilter(occasions.occasions, occasions.filter).slice(0, occasions.limit)) ||
            []
        );
    },
);

export const filteredOccasionCount = createSelector(
    (state: RootState) => {
        return {occasions: state.occasions?.occasions, filter: state.occasions?.filter, limit: state.occasions?.limit};
    },
    (occasions) => {
        return (
            (occasions.occasions && occasions.filter && applyOccasionFilter(occasions.occasions, occasions.filter)) ||
            []
        ).length;
    },
);

export const filterOccasions = createAction('occasions/filterOccasions', (filter: Filters) => {
    return {
        payload: {
            filter,
        },
    };
});

export const showMoreOccasions = createAction('occasions/showMoreOccasions', (count: number) => {
    return {
        payload: {
            count,
        },
    };
});

export const loadOccasionsFromDatabase = createAsyncThunk('occasions/showMoreOccasions', async () => {
    const occasions = await getFromDatabase<Occasion[]>('occasions');
    return (occasions && occasions.sort((a, b) => (a.sort && b.sort ? a.sort - b.sort : 0))) || [];
});

export const fetchMoreOccasions = createAsyncThunk(
    'occasions/fetchMoreOccasions',
    async (data: {
        start: number;
        limit: number;
        brands?: string[];
        models?: string[];
        buildYearFrom?: number;
        buildYearTill?: number;
        priceFrom?: number;
        priceTill?: number;
        mileageFrom?: number;
        mileageTill?: number;
        transmission?: string;
        locationId?: number;
    }) => {
        const result = await Occasions.getOccasions(
            data.start,
            data.limit,
            data.brands,
            data.models,
            data.buildYearFrom,
            data.buildYearTill,
            data.priceFrom,
            data.priceTill,
            data.mileageFrom,
            data.mileageTill,
            data.transmission,
            data.locationId,
        );
        return result.data;
    },
);

export const occasionSelector = createSelector(
    (state: RootState) => state.occasions.occasions,
    (_: RootState, id: number | null) => id,
    (occasions, id) => occasions?.find((n) => id != null && n?.id === id) ?? undefined,
);

function mergedOccasions(
    oldOccasions?: OccasionsQuery['occasions'],
    newOccasions?: OccasionsQuery['occasions'],
): OccasionsQuery['occasions'] {
    const occasionsMap = new Map(oldOccasions?.map((msg) => [msg?.id, msg]));
    if (newOccasions) {
        for (const occasion of newOccasions) {
            if (occasion) {
                occasionsMap.set(occasion.id, occasion);
            }
        }
    }
    return Array.from(occasionsMap.values());
}

export const occasionsSlice = createSlice({
    name: 'occasions',
    initialState: () => {
        const storedDate = localStorage.getItem('last_occasion_fetch');
        const lastOccasionFetch = storedDate || null;
        const initialState: AsyncState<State> = {
            status: Status.IDLE,
            filteredOccasions: [],
            filter: emptyFilter,
            limit: 20,
            lastOccasionFetch: lastOccasionFetch,
        };
        return initialState as AsyncState<State>;
    },
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(fetchOccasions.pending, (state) => {
            state.status = Status.PENDING;
        });
        builder.addCase(fetchOccasions.fulfilled, (state, action) => {
            state.status = Status.FULFILLED;
            state.occasions = action.payload.occasions;
            state.occasionPageInfo = action.payload.occasionPageInfo;
            if (action.payload.occasions) {
                try {
                    void storeArrayInDatabase('occasions', action.payload.occasions as Occasion[]);
                    const date = formatISO(new Date());
                    localStorage.setItem('last_occasion_fetch', date);
                    state.lastOccasionFetch = date;
                } catch (e) {
                    console.error('Error while storing occasions in DB', e);
                    throw e;
                }
            }
            state.__typename = action.payload.__typename;
        });
        builder.addCase(fetchOccasions.rejected, (state, action) => {
            state.status = Status.REJECTED;
            (state as RejectedState<State>).error = action.error.message ?? 'Error';
        });

        builder.addCase(fetchMoreOccasions.pending, (state) => {
            state.status = Status.LOADING_MORE;
        });
        builder.addCase(fetchMoreOccasions.fulfilled, (state, action) => {
            state.status = Status.FULFILLED;
            state.occasions = mergedOccasions(state.occasions, action.payload.occasions);
            state.occasionPageInfo = action.payload.occasionPageInfo;
            state.__typename = action.payload.__typename;
        });
        builder.addCase(fetchMoreOccasions.rejected, (state, action) => {
            state.status = Status.REJECTED;
            (state as RejectedState<State>).error = action.error.message ?? 'Error';
        });
        builder.addCase(filterOccasions, (state, action) => {
            state.filter = action.payload.filter;
        });
        builder.addCase(showMoreOccasions, (state, action) => {
            state.limit = state.limit + action.payload.count;
        });
        builder.addCase(loadOccasionsFromDatabase.fulfilled, (state, action) => {
            state.occasions = action.payload;
        });
    },
});
